import React, { useState } from "react";
import { Link } from "react-router-dom";
import {Search, Calendar, ArrowRight, BookOpen, MapPin, Shield, Syringe, Globe, DollarSign, Clock} from 'lucide-react';
import { motion } from "framer-motion";

interface BlogArticle {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  image: string;
  slug: string;
  icon: any;
  keywords: string[];
}

const Resources: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", "Vaccines", "Destinations", "Planning", "Costs", "Requirements"];

  const blogArticles: BlogArticle[] = [
    {
      id: "1",
      title: "Complete Guide to Travel Vaccines in Canada 2024",
      excerpt: "Everything you need to know about travel vaccines available in Canada, including costs, coverage, and booking procedures for international travelers.",
      category: "Vaccines",
      readTime: "8 min read",
      date: "2024-01-15",
      image: "https://static.lumi.new/6a/6a6889e1c5dedfdfe340214de1f4eee9.jpeg",
      slug: "travel-vaccines-canada-guide",
      icon: Syringe,
      keywords: ["travel vaccines Canada", "vaccine cost Canada", "travel immunization"]
    },
    {
      id: "2",
      title: "Do I Need Vaccines for India? Complete Travel Health Guide",
      excerpt: "Essential vaccinations and health preparations for traveling to India from British Columbia. Learn about hepatitis A, typhoid, malaria prevention, and more.",
      category: "Destinations",
      readTime: "10 min read",
      date: "2024-01-12",
      image: "https://static.lumi.new/5b/5b378f7338ebb6f2002b61a514e4161d.png",
      slug: "vaccines-needed-india-travel",
      icon: MapPin,
      keywords: ["vaccines for India", "India travel health", "typhoid vaccine India"]
    },
    {
      id: "3",
      title: "Yellow Fever Vaccine Requirements by Country 2024",
      excerpt: "Comprehensive list of countries requiring yellow fever vaccination certificates. Find out if your destination requires proof of yellow fever vaccination.",
      category: "Requirements",
      readTime: "7 min read",
      date: "2024-01-10",
      image: "https://static.lumi.new/c3/c35b33fc26af799b9204702f07e329c9.jpeg",
      slug: "yellow-fever-vaccine-requirements",
      icon: Shield,
      keywords: ["yellow fever vaccine", "yellow fever requirements", "vaccine certificate"]
    },
    {
      id: "4",
      title: "Hepatitis A Vaccine for Travel: Cost, Timing & Coverage",
      excerpt: "The most common travel vaccine explained: When to get it, how much it costs in Canada, and which destinations require hepatitis A protection.",
      category: "Vaccines",
      readTime: "6 min read",
      date: "2024-01-08",
      image: "https://static.lumi.new/2c/2c91dc7cec3ecdd4367a725546a08f79.png",
      slug: "hepatitis-a-vaccine-travel-guide",
      icon: Syringe,
      keywords: ["hepatitis A vaccine", "hep A vaccine cost", "hepatitis A travel"]
    },
    {
      id: "5",
      title: "How Long Before Travel Do I Need Vaccines?",
      excerpt: "Complete vaccination timeline guide. Learn when to book your travel health consultation and get vaccinated for maximum protection before departure.",
      category: "Planning",
      readTime: "5 min read",
      date: "2024-01-05",
      image: "https://static.lumi.new/9c/9c2f4377f47a152e60a3e0bba926493f.jpeg",
      slug: "travel-vaccine-timing-guide",
      icon: Clock,
      keywords: ["when to get travel vaccines", "vaccine timeline", "travel health planning"]
    },
    {
      id: "6",
      title: "Southeast Asia Backpacking: Essential Vaccines & Health Tips",
      excerpt: "Complete health preparation guide for backpacking Thailand, Vietnam, Cambodia, and beyond. Vaccines, malaria prevention, and safety tips.",
      category: "Destinations",
      readTime: "12 min read",
      date: "2024-01-03",
      image: "https://static.lumi.new/4c/4c0e3c45949c846acd9700bee6474d27.jpeg",
      slug: "southeast-asia-backpacking-vaccines",
      icon: Globe,
      keywords: ["Southeast Asia vaccines", "Thailand travel health", "backpacking vaccines"]
    },
    {
      id: "7",
      title: "Travel Vaccine Cost Breakdown: Canada 2024 Pricing",
      excerpt: "Detailed pricing guide for all travel vaccines in Canada. Find out which vaccines are covered by public health and what you'll need to pay out-of-pocket.",
      category: "Costs",
      readTime: "9 min read",
      date: "2024-01-01",
      image: "https://static.lumi.new/68/6821df5b0b43d51f53f409aac13c791b.jpeg",
      slug: "travel-vaccine-cost-canada",
      icon: DollarSign,
      keywords: ["vaccine cost Canada", "travel vaccine prices", "immunization costs"]
    },
    {
      id: "8",
      title: "Virtual Travel Health Consultation vs In-Person Clinic",
      excerpt: "Compare virtual and traditional travel health services. Learn the benefits of online consultations and when you might need an in-person visit.",
      category: "Planning",
      readTime: "7 min read",
      date: "2023-12-28",
      image: "https://static.lumi.new/20/2006983afc4b29039e33a0adff46a92a.png",
      slug: "virtual-vs-inperson-travel-clinic",
      icon: BookOpen,
      keywords: ["virtual travel clinic", "online travel health", "telehealth travel medicine"]
    },
    {
      id: "9",
      title: "Malaria Prevention: Medications & Protection Strategies",
      excerpt: "Everything you need to know about malaria prevention for travel to Africa, Asia, and Latin America. Compare medications and learn prevention strategies.",
      category: "Destinations",
      readTime: "11 min read",
      date: "2023-12-25",
      image: "https://static.lumi.new/3a/3a810d066b9dd0cb10ffbabee4520129.jpeg",
      slug: "malaria-prevention-travel-guide",
      icon: Shield,
      keywords: ["malaria prevention", "malaria medication", "antimalarial drugs"]
    },
    {
      id: "10",
      title: "Hajj & Umrah Health Requirements: Essential Vaccinations",
      excerpt: "Complete guide to mandatory and recommended vaccines for Hajj and Umrah pilgrims. Learn about meningococcal requirements and travel health tips.",
      category: "Requirements",
      readTime: "8 min read",
      date: "2023-12-22",
      image: "https://static.lumi.new/5b/5ba89e1ba0076ea3a5861a15d7565225.jpeg",
      slug: "hajj-umrah-vaccination-requirements",
      icon: Globe,
      keywords: ["Hajj vaccination", "Umrah health requirements", "meningococcal vaccine Saudi Arabia"]
    }
  ];

  const filteredArticles = blogArticles.filter(article => {
    const matchesCategory = selectedCategory === "All" || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.keywords.some(keyword => keyword.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-navy-900 dark:to-navy-800">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-blue-500/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-navy-900 via-blue-600 to-cyan-500 dark:from-white dark:via-cyan-400 dark:to-blue-400 bg-clip-text text-transparent">
              Travel Health Resources
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              Expert guides, destination-specific advice, and everything you need to know about staying healthy while traveling abroad.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto mb-8">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search articles, vaccines, destinations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-gray-200 dark:border-white/10 bg-white dark:bg-navy-800 focus:border-cyan-500 dark:focus:border-cyan-400 outline-none transition-all text-gray-900 dark:text-white"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2 rounded-full font-medium transition-all ${
                    selectedCategory === category
                      ? "bg-cyan-500 text-white shadow-lg shadow-cyan-500/25"
                      : "bg-white dark:bg-navy-800 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-white/10 hover:border-cyan-500 dark:hover:border-cyan-400"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => {
              const IconComponent = article.icon;
              return (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group bg-white dark:bg-navy-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10 hover:border-cyan-500/50 dark:hover:border-cyan-400/50 transition-all hover:shadow-xl hover:shadow-cyan-500/10"
                >
                  {/* Article Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 bg-cyan-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      {article.category}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>

                  {/* Article Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(article.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {article.readTime}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                      {article.title}
                    </h3>

                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>

                    <Link
                      to={`/resources/${article.slug}`}
                      className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 font-semibold text-sm group-hover:gap-3 transition-all"
                    >
                      Read Article
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No articles found</h3>
              <p className="text-gray-600 dark:text-gray-400">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-500 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Prepare for Your Trip?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Book a virtual consultation with our travel health experts and get personalized recommendations for your destination.
          </p>
          <a
            href="https://voyagevax.janeapp.com/locations/voyagevax-langley/book#/discipline/1/treatment/4"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-cyan-600 font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
          >
            Book Virtual Consult
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Resources;
